import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MoveHorizontal } from "lucide-react";
import { useLenis } from "lenis/react";
import { PORTFOLIO } from "@/data/content";

export default function HorizontalPortfolio() {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const x = useTransform(scrollYProgress, [0, 1], ["0vw", "-207vw"]);
  const barW = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  const goContact = () => {
    const el = document.getElementById("contact");
    if (lenis && el) lenis.scrollTo(el, { offset: -20 });
    else el?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="work"
      ref={ref}
      className="relative bg-black"
      style={{ height: "400vh" }}
      data-testid="portfolio"
    >
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <motion.div style={{ x }} className="flex items-stretch gap-[3vw] pl-[6vw] will-change-transform">
          {/* intro panel */}
          <div className="flex w-[80vw] shrink-0 flex-col justify-center pr-8 sm:w-[38vw]">
            <span className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500">
              <span className="h-px w-10 bg-zinc-600" />
              Selected Work
            </span>
            <h2 className="font-display text-5xl sm:text-6xl font-bold uppercase leading-[0.9] tracking-tight text-white">
              Shows we brought to life
            </h2>
            <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-zinc-500">
              Arenas, festivals, broadcasts and awards. Keep scrolling to move
              sideways through the darkness we&apos;ve engineered for.
            </p>
            <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-600">
              <MoveHorizontal className="h-4 w-4 animate-pulse" />
              Scroll to explore
            </div>
          </div>

          {/* project cards */}
          {PORTFOLIO.map((p, i) => (
            <article
              key={p.id}
              data-testid={`project-${p.id}`}
              data-cursor="hover"
              className="group relative w-[86vw] shrink-0 overflow-hidden rounded-2xl border border-white/10 sm:w-[42vw]"
            >
              <div className="relative h-full w-full overflow-hidden bg-[#050505]">
                {p.image ? (
                  <>
                    <img
                      src={p.image}
                      alt={p.title}
                      className="h-full w-full object-cover grayscale transition-all duration-[900ms] ease-out group-hover:scale-105 group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent opacity-90" />
                  </>
                ) : (
                  <div className="absolute inset-0 bg-[#050505]">
                    {/* stage spill from top */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "radial-gradient(120% 60% at 30% -10%, rgba(255,255,255,0.24), transparent 55%), radial-gradient(100% 60% at 75% -8%, rgba(228,228,231,0.18), transparent 60%)",
                      }}
                    />
                    {/* truss silhouette across the top */}
                    <div className="absolute inset-x-6 top-[14%] h-[3px] bg-white/25" />
                    <div className="absolute inset-x-6 top-[16%] h-[1px] bg-white/15" />
                    {[10, 24, 38, 52, 66, 80].map((leftPct) => (
                      <div
                        key={leftPct}
                        className="absolute top-[16%] h-3 w-[2px] bg-white/25"
                        style={{ left: `${leftPct}%` }}
                      />
                    ))}
                    {/* two crossed beams */}
                    <div
                      className="absolute top-[16%] left-[20%] h-[75%] w-[8px] origin-top -rotate-[10deg]"
                      style={{
                        background:
                          "linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.05) 55%, transparent 80%)",
                        filter: "blur(3px)",
                      }}
                    />
                    <div
                      className="absolute top-[16%] left-[64%] h-[75%] w-[8px] origin-top rotate-[8deg]"
                      style={{
                        background:
                          "linear-gradient(rgba(255,255,255,0.50), rgba(255,255,255,0.05) 55%, transparent 80%)",
                        filter: "blur(3px)",
                      }}
                    />
                    {/* base flare */}
                    <div
                      className="absolute left-1/2 top-[78%] h-40 w-40 -translate-x-1/2 rounded-full"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(255,255,255,0.18), rgba(255,255,255,0) 60%)",
                        filter: "blur(2px)",
                      }}
                    />
                    {/* floor line */}
                    <div className="absolute inset-x-0 bottom-[22%] h-px bg-white/15" />
                    {/* fine dust */}
                    <div
                      className="absolute inset-0 opacity-[0.05]"
                      style={{
                        backgroundImage:
                          "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1.4px)",
                        backgroundSize: "38px 38px",
                      }}
                    />
                    {/* bottom vignette so text reads */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-transparent to-transparent" />
                  </div>
                )}
              </div>
              <div className="absolute inset-0 flex flex-col justify-between p-8 sm:p-10">
                <span className="font-display text-6xl font-extrabold text-white/10">
                  0{i + 1}
                </span>
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-400">
                      {p.category} · {p.year}
                    </span>
                    <h3 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-white">
                      {p.title}
                    </h3>
                  </div>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/25 text-white opacity-0 transition-all duration-500 group-hover:bg-white group-hover:text-black group-hover:opacity-100">
                    <ArrowUpRight className="h-5 w-5" />
                  </span>
                </div>
              </div>
            </article>
          ))}

          {/* outro CTA panel */}
          <div className="flex w-[80vw] shrink-0 flex-col justify-center pr-[6vw] sm:w-[34vw]">
            <h3 className="font-display text-4xl sm:text-5xl font-bold uppercase leading-[0.95] tracking-tight text-chrome">
              Your show is next
            </h3>
            <p className="mt-5 max-w-xs text-sm font-light leading-relaxed text-zinc-500">
              Tell us the venue, the date and the ambition. We&apos;ll bring the
              light.
            </p>
            <button
              onClick={goContact}
              data-testid="portfolio-cta"
              className="mt-8 inline-flex w-fit items-center gap-3 rounded-full bg-white px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-zinc-200"
            >
              Start a Project
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>

        {/* progress bar */}
        <div className="absolute bottom-10 left-[6vw] right-[6vw] h-px bg-white/10">
          <motion.div style={{ width: barW }} className="h-full bg-white" />
        </div>
      </div>
    </section>
  );
}
